// The friends list, shared by the Friends page (#/friends) and the player
// screen's Friends tab: one ruled row per friend with their trainer, guild tag,
// level, online state or when they were last seen, and the quick actions.
// Online first, then the most recently seen.
import { SOCIAL, type FriendRow, type SocialDTO } from '../shared/social.ts';
import { serverNow } from './state.ts';
import { esc, full, left } from './fmt.ts';
import { playerIcon } from './playericon.ts';
import { tagChip } from './guild.ts';
import { isArmed } from './confirm.ts';
import { playerLink } from './social.ts';
import { withSampleFriends, isSample, sampleName } from './sample.ts';

type Row = FriendRow & { seen?: number };

/** "Seen 5 min ago", "Seen 3 h ago", "Seen 2 days ago"; plain "Offline" when unknown. */
export function seenText(f: Row, now = Date.now()): string {
  if (f.online) return 'Online';
  if (!f.seen) return 'Offline';
  const m = Math.max(1, Math.round((now - f.seen) / 60_000));
  if (m < 60) return `Seen ${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `Seen ${h} h ago`;
  const d = Math.round(h / 24);
  return `Seen ${d} ${d === 1 ? 'day' : 'days'} ago`;
}

/** Why Challenge is off for this friend, or '' when it works. */
function challengeBlock(d: SocialDTO, online: boolean): string {
  const now = serverNow();
  if (!online) return 'Offline';
  if (d.friendlyAt > now) return `Your next friendly battle can start in ${left(d.friendlyAt - now)}`;
  if (d.invites.some((i) => i.mine)) return 'Invite pending';
  return '';
}

export interface FriendListOpts {
  /** More buttons on a real friend's row (the hub's Send coins). */
  extra?: (f: FriendRow) => string;
  /** Rows under a real friend's row (the hub's open gift form). */
  after?: (f: FriendRow) => string;
  /** The pager id for the list (client/fitlist.ts). */
  fit?: string;
}

/** The friends as shown (sample rows mixed in when an admin turned Sample data on), online first. */
export function shownFriends(d: SocialDTO): Row[] {
  return [...withSampleFriends(d.friends)].sort((a, b) => Number(b.online) - Number(a.online) || ((b as Row).seen ?? 0) - ((a as Row).seen ?? 0) || a.name.localeCompare(b.name));
}

function row(f: Row, d: SocialDTO, o: FriendListOpts): string {
  const fake = isSample(f);
  const name = fake ? sampleName(f.name) : playerLink(f.name, `<b>${esc(f.name)}</b>`);
  const state = `<span class="fl-state${f.online ? ' on' : ''}"${f.seen ? ` title="${esc(new Date(f.seen).toLocaleString())}"` : ''}><i class="gdot${f.online ? '' : ' off'}"></i>${seenText(f)}</span>`;
  let acts: string;
  if (fake) acts = `<button class="btn tiny" data-act="sample-row">Message</button><button class="btn tiny" data-act="sample-row">Challenge</button>`;
  else {
    const block = challengeBlock(d, f.online);
    const armed = isArmed(`unfriend:${f.name}`);
    acts = `<a class="btn tiny" href="#/chat/dm/${encodeURIComponent(f.name)}">Message${f.unread ? ` <span class="unread">${f.unread}</span>` : ''}</a>
      <button class="btn tiny" data-act="fr-invite" data-id="${esc(f.name)}"${block ? ` disabled title="${esc(block)}"` : ''}>Challenge</button>
      ${o.extra?.(f) ?? ''}
      <button class="btn tiny fl-rm${armed ? ' danger' : ''}" data-act="fr-remove" data-id="${esc(f.name)}" title="Remove ${esc(f.name)} from friends">${armed ? 'Confirm remove' : 'Remove'}</button>`;
  }
  return `<li class="fl-row${f.online ? ' on' : ''}${fake ? ' smp' : ''}" data-key="f-${esc(f.name)}">
    ${playerIcon({ outfit: f.outfit, size: 'md', name: f.name })}
    <div class="fl-who"><span class="fl-nm">${tagChip(f.tag)}${name}</span><span class="fl-meta"><span class="fl-lv">Lv ${full(f.level)}</span>${state}${!fake && f.unread ? `<span class="fl-unread">${f.unread} new</span>` : ''}</span></div>
    <div class="fl-acts">${acts}</div></li>${fake ? '' : o.after?.(f) ?? ''}`;
}

/** The list in a panel: a count line and the rows, or one quiet line when there are none. */
export function friendList(d: SocialDTO, o: FriendListOpts = {}, head = ''): string {
  const list = shownFriends(d);
  const online = list.filter((f) => f.online).length;
  return `<section class="panel fl-panel"><div class="fl-h"><h4>Friends</h4><span class="fl-count">${full(online)} online · ${full(list.length)} / ${SOCIAL.friendsMax}</span>${head}</div>
    ${list.length ? `<ul class="fl-list" data-fit="${o.fit ?? 'friends'}">${list.map((f) => row(f, d, o)).join('')}</ul>` : '<p class="fl-empty">No friends yet</p>'}</section>`;
}
